import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileSearch, Sparkles, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function ClaimReader() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Claim Reader</h1>
        <p className="text-muted-foreground mt-1">
          AI-powered insurance claim document analysis
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Claim Document
          </CardTitle>
          <CardDescription>
            Upload insurance claim or policy documents (PDF, DOCX)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer">
            <FileSearch className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Drop document here or click to upload</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Supports PDF, DOCX - Max 25MB
            </p>
            <Button variant="outline">
              <Upload className="w-4 h-4 mr-2" />
              Select Document
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Parsed Policy Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50 opacity-30">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">Policy Number</span>
                  <Badge variant="outline">Detected</Badge>
                </div>
                <p className="text-sm text-muted-foreground">XXXX-XXXX-XXXX</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 opacity-30">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">Claim Number</span>
                  <Badge variant="outline">Detected</Badge>
                </div>
                <p className="text-sm text-muted-foreground">CLM-XXXX-XXXX</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 opacity-30">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">Coverage Amount</span>
                  <Badge variant="outline">Detected</Badge>
                </div>
                <p className="text-sm text-muted-foreground">$XX,XXX</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 opacity-30">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">Deductible</span>
                  <Badge variant="outline">Detected</Badge>
                </div>
                <p className="text-sm text-muted-foreground">$X,XXX</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coverage Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-success/10 opacity-30">
                <CheckCircle2 className="w-5 h-5 text-success mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Roof Replacement</p>
                  <p className="text-xs text-muted-foreground">Covered under policy</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-success/10 opacity-30">
                <CheckCircle2 className="w-5 h-5 text-success mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Wind Damage</p>
                  <p className="text-xs text-muted-foreground">Covered under policy</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 opacity-30">
                <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Wear & Tear</p>
                  <p className="text-xs text-muted-foreground">Not covered</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 opacity-30">
                <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Missing Items</p>
                  <p className="text-xs text-muted-foreground">3 items not mentioned in claim</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Module Notice */}
      <Card className="border-primary/20 bg-card/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Claim Reader Engine Coming Soon</h3>
              <p className="text-sm text-muted-foreground">
                Advanced NLP and document parsing will automatically extract policy details,
                coverage amounts, exclusions, and identify missing items from insurance claims.
                Compare estimates against policy coverage in seconds.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
